'use client';

import { useState, useEffect, useRef } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { useSearch } from '@/hooks/useSearch';
import { Spinner } from '@/components/ui/Spinner';
import { EntryCard } from '@/components/catalog/EntryCard';
import { LibraryOfflineCard } from '@/components/feedback/LibraryOfflineCard';
import { isUpstreamDown } from '@/lib/utils/opds-errors';
import type { LibraryConnection } from '@/lib/opds/types';

export function SearchDialog({
  open,
  onClose,
  library,
  searchDescUrl,
}: {
  open: boolean;
  onClose: () => void;
  library: LibraryConnection;
  searchDescUrl?: string;
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { results, resultsFeedUrl, loading, error, search, clearResults } = useSearch(library, searchDescUrl);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      clearResults();
    }
  }, [open, clearResults]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) search(query.trim());
  };

  return (
    <Dialog open={open} onClose={onClose} title="Поиск книг">
      <form onSubmit={handleSubmit} className="mb-4">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Название, автор, серия…"
            className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={!query.trim() || loading}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Найти
          </button>
        </div>
      </form>

      {loading && <Spinner className="py-8" />}
      {error && (
        isUpstreamDown(error) ? (
          <LibraryOfflineCard />
        ) : (
          <p className="text-sm text-red-600">{error}</p>
        )
      )}

      {results && results.entries.length === 0 && (
        <p className="py-4 text-center text-sm text-neutral-500">Ничего не найдено</p>
      )}

      {results && results.entries.length > 0 && (
        <div className="max-h-96 overflow-y-auto">
          {(() => {
            const navEntries = results.entries.filter((e) =>
              e.acquisitionLinks.length === 0 &&
              e.links.some((l) => l.type?.includes('opds-catalog') || l.rel === 'subsection')
            );
            const bookEntries = results.entries.filter((e) =>
              e.acquisitionLinks.length > 0 ||
              !e.links.some((l) => l.type?.includes('opds-catalog') || l.rel === 'subsection')
            );
            return (
              <>
                {navEntries.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {navEntries.map((entry) => (
                      <div key={entry.id} onClick={onClose}>
                        <EntryCard entry={entry} library={library} isNavigation />
                      </div>
                    ))}
                  </div>
                )}
                {bookEntries.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {bookEntries.map((entry, i) => (
                      <div key={entry.id} onClick={onClose}>
                        <EntryCard entry={entry} library={library} feedUrl={resultsFeedUrl} entryIndex={i} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </Dialog>
  );
}
